import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChevronRight, 
  ShoppingCart, 
  Check, 
  Leaf, 
  Droplet, 
  ChevronDown,
  ChevronUp,
  Package,
  Award,
  Clock,
  Gift,
  X,
  ChevronLeft,
  ZoomIn,
  Truck,
  AlertCircle
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { getPriceInfo, isDiscountActive, getDiscountPercentage } from '../config/discountConfig';
import { getProductStock, STOCK_STATUS } from '../config/productConfig';
import RelatedProducts from '../components/RelatedProducts';
import stirrerImg from '../assets/images/stirrer.jpg';
import tastymaxi_13 from "../assets/Cutlery/tastymaxi_13.jpg";
import tastymaxi_23 from "../assets/Cutlery/tastymaxi_23.jpg";

const EdibleCoffeeStirrer = () => {
  const [selectedQuantity, setSelectedQuantity] = useState('100');
  const [selectedFlavor, setSelectedFlavor] = useState('vanilla');
  const [activeTab, setActiveTab] = useState('description');
  const [selectedImage, setSelectedImage] = useState(0);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [isQuantityDropdownOpen, setIsQuantityDropdownOpen] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxImageIndex, setLightboxImageIndex] = useState(0);
  const [isAddedToCart, setIsAddedToCart] = useState(false);
  
  const { addToCart } = useCart();
  
  // Get stock status from config (use the flavor-specific one if available)
  const stockInfo = getProductStock(`edible-coffee-stirrer-${selectedFlavor}`) || getProductStock('edible-coffee-stirrer');

  // Flavor options with price modifiers (Chocolate is premium, Vanilla is base)
  const flavorOptions = [
    { id: 'chocolate', name: 'Chocolate', priceModifier: 0.50 },
    { id: 'vanilla', name: 'Vanilla', priceModifier: 0 }
  ];

  const getSelectedFlavor = () => {
    return flavorOptions.find(f => f.id === selectedFlavor) || flavorOptions[1];
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isLightboxOpen) return;
      
      if (e.key === 'Escape') {
        setIsLightboxOpen(false);
      } else if (e.key === 'ArrowRight') {
        setLightboxImageIndex((prev) => (prev + 1) % productImages.length);
      } else if (e.key === 'ArrowLeft') {
        setLightboxImageIndex((prev) => (prev - 1 + productImages.length) % productImages.length);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen]);

  useEffect(() => {
    if (isLightboxOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isLightboxOpen]);

  const openLightbox = (index) => {
    setLightboxImageIndex(index);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
  };

  const goToNextImage = () => {
    setLightboxImageIndex((prev) => (prev + 1) % productImages.length);
  };

  const goToPrevImage = () => {
    setLightboxImageIndex((prev) => (prev - 1 + productImages.length) % productImages.length);
  };

  const pricingOptions = [
    { units: 'sample', price: 0, label: 'Sample', description: 'Free sample' },
    { units: 30, price: 3.2, label: '100 stirrers', description: '1 pack' },
  ];

  const getSelectedPricing = () => {
    const basePricing = pricingOptions.find(opt => String(opt.units) === selectedQuantity) || pricingOptions[1];
    const flavorModifier = getSelectedFlavor().priceModifier;
    
    // Apply flavor price modifier (per pack of 100)
    if (basePricing.units === 'sample') {
      return basePricing;
    }
    
    const packCount = basePricing.units / 100;
    const totalFlavorAddon = flavorModifier * 2*packCount;
    
    return {
      ...basePricing,
      price: basePricing.price + totalFlavorAddon,
      basePrice: basePricing.price
    };
  };

  const selectedPricing = getSelectedPricing();
  const currentFlavor = getSelectedFlavor();

  const productImages = [
    { id: 0, src: stirrerImg, alt: "Edible Coffee Stirrer - Main Product View" },
    { id: 1, src: tastymaxi_13, alt: "Edible Coffee Stirrer - Detail View" },
    { id: 2, src: tastymaxi_23, alt: "Edible Coffee Stirrer - In Use" }
  ];

  const product = {
    name: "Edible Coffee Stirrer",
    subtitle: "The Sustainable Alternative to Wood Stir Sticks",
    sku: "CUTLERY-STIRRER-001",
    unitsPerPack: 30
  };

  const keyFeatures = [
    "Perfect for coffee & hot beverages",
    "XL version available for tall glasses",
    "Replaces conventional wood stir sticks",
    "Pleasant taste that complements coffee",
    "100% Edible & Biodegradable EATlery® by FRENVI made in Germany"
  ];

  const productDetails = {
    dimensions: {
      length: "14 cm (Standard) / 19 cm (XL)",
      width: "0.8 cm",
      weight: "4.8g per stirrer",
      style: "Slim stick design"
    },
ingredients: [
  "Wheat flour, Sorghum flour, Brown sugar",
  "Invert sugar syrup, Refined wheat flour, Rice bran oil",
  "Wheat protein, Soy lecithin, Iodized salt",
  "Vanillin, Potassium carbonate, Added antioxidants",
  "Natural flavors (vanilla/chocolate)"
],
nutritionalInfo: {
  calories: "19.58 kcal",
  fat: "0.22 g",
  monounsaturatedFat: "0.307 g",
  carbohydrates: "3.89 g",
  sugars: "0.86 g",
  fiber: "0.44 g",
  protein: "0.51 g",
  salt: "0.0045 g"
},
    storage: "Store in a cool, dry place (15-25°C) away from direct sunlight. Keep in original packaging until use. Best consumed within 18 months of production date.",
    certifications: ["ISO 22000", "FSSAI Certified", "FDA Approved", "Vegan Friendly"],
    shelfLife: "18 months from production date"
  };

  const faqs = [
    {
      question: "How long can the stirrer stay in hot coffee?",
      answer: "Our edible coffee stirrers are designed to withstand hot beverages up to 85°C. They maintain their structure for stirring purposes while gradually absorbing some coffee flavor, making them delicious to eat afterwards."
    },
{
  question: "Is the edible stirrer suitable for larger coffee glasses?",
  answer: "Yes. The 14 cm version is designed specifically for tall latte macchiato glasses and large specialty drinks. It is sturdy enough to stir hot beverages and holds its shape throughout use."
},
    {
      question: "Do the stirrers have any flavor?",
      answer: "Yes! Our stirrers have a subtle, pleasant taste that complements coffee perfectly. They're lightly sweetened and have a hint of natural coffee flavoring."
    },
    {
      question: "Are these better than wooden stir sticks?",
      answer: "Absolutely! Unlike wooden sticks, our edible stirrers are fully consumable, leaving zero waste. They also don't impart any unwanted woody taste to your beverage."
    }
  ];

  return (
    <div className="edible-cup-page">
      <nav className="breadcrumb-nav">
        <div className="container">
          <ol className="breadcrumb">
            <li><Link to="/">Home</Link></li>
            <ChevronRight size={14} />
            <li><Link to="/cutlery-tableware">Cutlery & Tableware</Link></li>
            <ChevronRight size={14} />
            <li className="current">Edible Coffee Stirrer</li>
          </ol>
        </div>
      </nav>

      <section className="product-main-section">
        <div className="container">
          <div className="product-layout">
            <div className="product-gallery">
              <div className="main-image-container" onClick={() => openLightbox(selectedImage)}>
                <span className="zoom-hint">
                  <ZoomIn size={20} />
                  Click to zoom
                </span>
                <img 
                  src={productImages[selectedImage].src} 
                  alt={productImages[selectedImage].alt}
                  className="main-product-image clickable"
                />
              </div>
              <div className="thumbnail-gallery">
                {productImages.map((img, index) => (
                  <button
                    key={img.id}
                    className={`thumbnail-btn ${selectedImage === index ? 'active' : ''}`}
                    onClick={() => setSelectedImage(index)}
                    onDoubleClick={() => openLightbox(index)}
                  >
                    <img src={img.src} alt={img.alt} />
                  </button>
                ))}
              </div>
              
              {/* Key Features - Under Image */}
              <div className="key-features">
                <h3>Key Features</h3>
                <ul>
                  {keyFeatures.map((feature, index) => (
                    <li key={index}>
                      <Leaf size={16} className="feature-icon" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="product-info">
              <span className="product-sku">SKU: {product.sku}</span>
              <h1 className="product-title">{product.name}</h1>
              <p className="product-subtitle">{product.subtitle}</p>

              <div className="carton-pricing">
                {selectedPricing.units === 'sample' ? (
                  <div className="price-main">
                    <span className="price free">FREE</span>
                    <span className="price-label">Sample</span>
                  </div>
                ) : (
                  <>
                    {(() => {
                      const priceInfo = getPriceInfo(selectedPricing.price);
                      return (
                        <div className={`price-main ${priceInfo.hasDiscount ? 'has-discount' : ''}`}>
                          {priceInfo.hasDiscount && (
                            <>
                              <span className="discount-badge">-{priceInfo.discountPercentage}</span>
                              <span className="price-original">€{priceInfo.originalPrice.toFixed(2)}</span>
                            </>
                          )}
                          <span className={`price ${priceInfo.hasDiscount ? 'discounted' : ''}`}>
                            €{priceInfo.discountedPrice.toFixed(2)}
                          </span>
                          <span className="price-label">for {selectedPricing.units} stirrers</span>
                        </div>
                      );
                    })()}
                  </>
                )}
                <div className={`stock-status ${stockInfo.isSoldOut ? 'sold-out' : 'in-stock'}`}>
                  {stockInfo.isSoldOut ? (
                    <>
                      <AlertCircle size={20} />
                      {stockInfo.statusLabel}
                    </>
                  ) : (
                    <>
                      <Check size={20} />
                      {stockInfo.statusLabel}
                    </>
                  )}
                </div>
                {stockInfo.isInStock && (
                  <div className="delivery-time">
                    <Truck size={16} />
                    <span>Delivery: {stockInfo.deliveryTime}</span>
                  </div>
                )}
              </div>

              <div className="carton-info-box">
                <Package size={20} />
                <div>
                  <strong>Sold by Pack</strong>
                  <span>Each pack contains {product.unitsPerPack} stirrers</span>
                </div>
              </div>

              <div className="size-info-box">
                <Leaf size={20} />
                <div>
                  <strong>Zero Waste Alternative</strong>
                  <span>Replace wooden sticks with edible stirrers</span>
                </div>
              </div>

              {/* Flavor Selection */}
              <div className="flavor-selector">
                <label className="flavor-label">Select Flavor:</label>
                <div className="flavor-options">
                  {flavorOptions.map((flavor) => (
                    <button
                      key={flavor.id}
                      className={`flavor-option ${selectedFlavor === flavor.id ? 'active' : ''}`}
                      onClick={() => setSelectedFlavor(flavor.id)}
                    >
                      <span className="flavor-radio">
                        {selectedFlavor === flavor.id && <Check size={14} />}
                      </span>
                      <span className="flavor-name">{flavor.name}</span>
                    </button>
                  ))}
                </div>
                <div className="selected-flavor-display">
                  <span>Selected: <strong>{currentFlavor.name}</strong></span>
                </div>
              </div>

              <div className="purchase-controls-carton">
                <div className="quantity-dropdown-container">
                  <label>Select Quantity:</label>
                  <div className="quantity-dropdown">
                    <button 
                      className="quantity-dropdown-trigger"
                      onClick={() => setIsQuantityDropdownOpen(!isQuantityDropdownOpen)}
                    >
                      <span>
                        {selectedPricing.units === 'sample' ? (
                          <>
                            <Gift size={16} />
                            Sample - FREE
                          </>
                        ) : (
                          <>
                            {(() => {
                              const priceInfo = getPriceInfo(selectedPricing.price);
                              return (
                                <>
                                  {selectedPricing.units} stirrers - 
                                  {priceInfo.hasDiscount && (
                                    <span className="dropdown-original-price">€{priceInfo.originalPrice.toFixed(2)}</span>
                                  )}
                                  <span className={priceInfo.hasDiscount ? 'dropdown-discounted-price' : ''}>
                                    €{priceInfo.discountedPrice.toFixed(2)}
                                  </span>
                                </>
                              );
                            })()}
                          </>
                        )}
                      </span>
                      <ChevronDown size={18} className={isQuantityDropdownOpen ? 'rotate' : ''} />
                    </button>
                    
                    {isQuantityDropdownOpen && (
                      <div className="quantity-dropdown-menu">
                        {pricingOptions.map((option) => (
                          <button
                            key={option.units}
                            className={`quantity-option ${String(option.units) === selectedQuantity ? 'active' : ''}`}
                            onClick={() => {
                              setSelectedQuantity(String(option.units));
                              setIsQuantityDropdownOpen(false);
                            }}
                          >
                            <div className="option-main">
                              {option.units === 'sample' ? (
                                <>
                                  <Gift size={16} />
                                  <span className="option-cups">Sample</span>
                                </>
                              ) : (
                                <>
                                  <span className="option-cups">{option.units} stirrers</span>
                                  <span className="option-cartons">({option.description})</span>
                                </>
                              )}
                            </div>
                            <div className="option-price">
                              {option.units === 'sample' ? (
                                <span className="free-tag">FREE</span>
                              ) : (
                                <>
                                  {(() => {
                                    const optPriceInfo = getPriceInfo(option.price);
                                    return (
                                      <>
                                        {optPriceInfo.hasDiscount && (
                                          <span className="option-original-price">€{optPriceInfo.originalPrice.toFixed(2)}</span>
                                        )}
                                        <span className={optPriceInfo.hasDiscount ? 'option-discounted-price' : ''}>
                                          €{optPriceInfo.discountedPrice.toFixed(2)}
                                        </span>
                                      </>
                                    );
                                  })()}
                                </>
                              )}
                            </div>
                            {String(option.units) === selectedQuantity && <Check size={16} className="check-icon" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <button 
                  className={`add-to-cart-btn ${isAddedToCart ? 'added' : ''} ${stockInfo.isSoldOut ? 'disabled sold-out-btn' : ''}`}
                  disabled={stockInfo.isSoldOut}
                  onClick={() => {
                    if (stockInfo.isSoldOut) return;
                    
                    if (selectedPricing.units === 'sample') {
                      window.location.href = '/contact';
                      return;
                    }
                    
                    const cartItem = {
                      id: `edible-coffee-stirrer-${selectedFlavor}`,
                      name: 'Edible Coffee Stirrer',
                      variant: `${selectedPricing.units} stirrers - ${currentFlavor.name}`,
                      flavor: currentFlavor.name,
                      price: selectedPricing.price,
                      units: selectedPricing.units,
                      quantity: 1,
                      image: productImages[0].src
                    };
                    
                    addToCart(cartItem);
                    setIsAddedToCart(true);
                    setTimeout(() => setIsAddedToCart(false), 2000);
                  }}
                >
                  {stockInfo.isSoldOut ? (
                    <>
                      <AlertCircle size={20} />
                      Sold Out
                    </>
                  ) : isAddedToCart ? (
                    <>
                      <Check size={20} />
                      Added to Cart!
                    </>
                  ) : (
                    <>
                      <ShoppingCart size={20} />
                      {selectedPricing.units === 'sample' ? (
                        'Request Free Sample'
                      ) : (
                        <>
                          {(() => {
                            const btnPriceInfo = getPriceInfo(selectedPricing.price);
                            return (
                              <>
                                Add to Cart - 
                                {btnPriceInfo.hasDiscount && (
                                  <span className="btn-original-price">€{btnPriceInfo.originalPrice.toFixed(2)}</span>
                                )}
                                <span className={btnPriceInfo.hasDiscount ? 'btn-discounted-price' : ''}>
                                  €{btnPriceInfo.discountedPrice.toFixed(2)}
                                </span>
                              </>
                            );
                          })()}
                        </>
                      )}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="product-tabs-section">
        <div className="container">
          <div className="tabs-navigation">
            <button 
              className={`tab-btn ${activeTab === 'description' ? 'active' : ''}`}
              onClick={() => setActiveTab('description')}
            >
              Description
            </button>
            <button 
              className={`tab-btn ${activeTab === 'details' ? 'active' : ''}`}
              onClick={() => setActiveTab('details')}
            >
              Product Details
            </button>
            <button 
              className={`tab-btn ${activeTab === 'faq' ? 'active' : ''}`}
              onClick={() => setActiveTab('faq')}
            >
              FAQ
            </button>
          </div>

          <div className="tab-content">
            {activeTab === 'description' && (
              <div className="tab-panel description-panel">
                <h2>Edible Coffee Stirrer - Sustainable Innovation</h2>
                <p>
                  The edible coffee stir sticks are the innovative, sustainable alternative to conventional wood stir sticks. Ideal for coffee specialties, and in the XL version perfect for tall latte macchiato glasses.
                </p>
                <p>
                  Unlike wooden or plastic stirrers that end up in landfills, our edible stirrers can be eaten after use, creating a truly zero-waste coffee experience. They're perfect for cafés, offices, events, and anyone who wants to reduce their environmental footprint.
                </p>
                <p>
                  With a subtle sweetness and coffee-complementing flavor, these stirrers add a touch of innovation to every cup.
                </p>
                
                <div className="description-features">
                  <div className="desc-feature">
                    <Package size={32} />
                    <h4>Two Sizes Available</h4>
                    <p>Standard 14cm for regular cups, XL 19cm for tall glasses.</p>
                  </div>
                  <div className="desc-feature">
                    <Droplet size={32} />
                    <h4>Heat Resistant</h4>
                    <p>Withstands hot beverages up to 85°C without melting.</p>
                  </div>
                  <div className="desc-feature">
                    <Leaf size={32} />
                    <h4>True Zero Waste</h4>
                    <p>Eat it, compost it, or let it biodegrade. No landfill waste.</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'details' && (
              <div className="tab-panel details-panel">
                <div className="details-grid">
                  <div className="detail-section">
                    <h3><Package size={20} /> Dimensions</h3>
                    <table className="details-table">
                      <tbody>
                        <tr><td>Length</td><td>{productDetails.dimensions.length}</td></tr>
                        <tr><td>Width</td><td>{productDetails.dimensions.width}</td></tr>
                        <tr><td>Weight</td><td>{productDetails.dimensions.weight}</td></tr>
                        <tr><td>Style</td><td>{productDetails.dimensions.style}</td></tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="detail-section">
                    <h3><Leaf size={20} /> Ingredients</h3>
                    <ul className="ingredients-list">
                      {productDetails.ingredients.map((ingredient, index) => (
                        <li key={index}>{ingredient}</li>
                      ))}
                    </ul>
                    <p className="allergen-warning">
                      <strong>Allergen Information:</strong> Contains wheat and gluten.
                    </p>
                  </div>

                  <div className="detail-section">
                    <h3><Award size={20} /> Nutritional Information (per stirrer)</h3>
                    <table className="details-table">
                      <tbody>
                        <tr><td>Energy</td><td>{productDetails.nutritionalInfo.calories}</td></tr>
                        <tr><td>Protein</td><td>{productDetails.nutritionalInfo.protein}</td></tr>
                        <tr><td>Carbohydrates</td><td>{productDetails.nutritionalInfo.carbohydrates}</td></tr>
                        <tr><td>Fat</td><td>{productDetails.nutritionalInfo.fat}</td></tr>
                        <tr><td>Fiber</td><td>{productDetails.nutritionalInfo.fiber}</td></tr>
                        <tr><td>Salt</td><td>{productDetails.nutritionalInfo.salt}</td></tr>
                        <tr><td>Sugars</td><td>{productDetails.nutritionalInfo.sugars}</td></tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="detail-section">
                    <h3><Clock size={20} /> Storage & Shelf Life</h3>
                    <p>{productDetails.storage}</p>
                    <p><strong>Shelf Life:</strong> {productDetails.shelfLife}</p>
                  </div>

                  <div className="detail-section full-width">
                    <h3><Award size={20} /> Certifications</h3>
                    <div className="certifications-list">
                      {productDetails.certifications.map((cert, index) => (
                        <span key={index} className="cert-badge">{cert}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'faq' && (
              <div className="tab-panel faq-panel">
                <h2>Frequently Asked Questions - Edible Coffee Stirrer</h2>
                <div className="faq-list">
                  {faqs.map((faq, index) => (
                    <div 
                      key={index} 
                      className={`faq-item ${expandedFaq === index ? 'expanded' : ''}`}
                    >
                      <button 
                        className="faq-question"
                        onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                      >
                        <span>{faq.question}</span>
                        {expandedFaq === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      </button>
                      {expandedFaq === index && (
                        <div className="faq-answer">
                          <p>{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Related Products Section */}
      <RelatedProducts currentProductId="coffee-stirrer" category="cutlery-tableware" />

      {isLightboxOpen && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>
              <X size={28} />
            </button>
            
            <button className="lightbox-nav lightbox-prev" onClick={goToPrevImage}>
              <ChevronLeft size={36} />
            </button>
            
            <div className="lightbox-image-container">
              <img 
                src={productImages[lightboxImageIndex].src} 
                alt={productImages[lightboxImageIndex].alt}
                className="lightbox-image"
              />
            </div>
            
            <button className="lightbox-nav lightbox-next" onClick={goToNextImage}>
              <ChevronRight size={36} />
            </button>
            
            <div className="lightbox-thumbnails">
              {productImages.map((img, index) => (
                <button
                  key={img.id}
                  className={`lightbox-thumb ${lightboxImageIndex === index ? 'active' : ''}`}
                  onClick={() => setLightboxImageIndex(index)}
                >
                  <img src={img.src} alt={img.alt} />
                </button>
              ))}
            </div>
            
            <div className="lightbox-counter">
              {lightboxImageIndex + 1} / {productImages.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EdibleCoffeeStirrer;
